import cx from 'classnames';
import {
  useCallback,
  useEffect,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import AiSparkleIcon from '@atlaskit/icon/core/ai-sparkle';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import { useIsReducedMotionEnabled } from '@trello/a11y';
import { Button } from '@trello/nachos/button';
import {
  Dialog,
  DialogCloseButton,
  type useDialog,
} from '@trello/nachos/dialog';
import { showFlag } from '@trello/nachos/experimental-flags';
import { navigate } from '@trello/router/navigate';
import {
  CSSTransitionWithRef,
  TransitionGroup,
} from '@trello/transition-group';
import { getMemberAiSettingsUrl } from '@trello/urls';

import { AllSet } from './OnboardPages/AllSet';
import { ConnectAccount } from './OnboardPages/ConnectAccount';
import { SmartScheduleSettings } from './OnboardPages/SmartScheduleSettings';
import { StartScheduling } from './OnboardPages/StartScheduling';
import { WorkingHours } from './OnboardPages/WorkingHours';
import { type SmartScheduleOnboardingEntryPoint } from './smartScheduleOnboardingSharedState';
import { useMemberHasValidPlannerAccount } from './useMemberHasValidPlannerAccount';
import {
  useSmartScheduleOnboardingAnalytics,
  type SmartScheduleOnboardingScreen,
} from './useSmartScheduleOnboardingAnalytics';
import {
  useUpsertSmartScheduleOnboardingAiRule,
  type SmartScheduleOnboardingAiRuleType,
} from './useUpsertSmartScheduleOnboardingAiRule';
import { useUpsertSmartScheduleOnboardingPreferences } from './useUpsertSmartScheduleOnboardingPreferences';

import * as styles from './SmartScheduleOnboardingDialog.module.less';

interface ConfirmedSecondaryRule {
  rule: string;
  type: SmartScheduleOnboardingAiRuleType;
}

interface SmartScheduleOnboardingDialogProps {
  dialogProps: ReturnType<typeof useDialog>['dialogProps'];
  onClose: () => void;
  onContinue: () => void;
  entryPoint: SmartScheduleOnboardingEntryPoint | null;
}

type PageTransitionDirection = 'backward' | 'forward';

type OnboardingPageKey =
  | 'allSet'
  | 'connect'
  | 'rules'
  | 'start'
  | 'workingHours';

const SCREEN_BY_KEY: Record<OnboardingPageKey, SmartScheduleOnboardingScreen> =
  {
    connect: 'connectAccountScreen',
    start: 'startScreen',
    workingHours: 'workingHoursScreen',
    rules: 'rulesScreen',
    allSet: 'allSetScreen',
  };

// Connect sits second; it's skipped during navigation when the member already
// has a connected account (see goToNextPage / handlePreviousPage).
const PAGES: ReadonlyArray<OnboardingPageKey> = [
  'start',
  'connect',
  'workingHours',
  'rules',
  'allSet',
];
const FINAL_PAGE = PAGES.length - 1;

export const SmartScheduleOnboardingDialog: FunctionComponent<
  SmartScheduleOnboardingDialogProps
> = ({ dialogProps, onClose, onContinue, entryPoint }) => {
  const intl = useIntl();
  const { upsertRule } = useUpsertSmartScheduleOnboardingAiRule();
  const { saveBoardPreference, saveProactiveStatus } =
    useUpsertSmartScheduleOnboardingPreferences();
  const isReducedMotionEnabled = useIsReducedMotionEnabled() === true;

  const {
    trackUIEvent,
    trackScreenViewed,
    trackModalViewed,
    trackCompleted,
    trackSaved,
  } = useSmartScheduleOnboardingAnalytics(entryPoint);

  const [activePage, setActivePage] = useState<number>(0);
  const [pageTransitionDirection, setPageTransitionDirection] =
    useState<PageTransitionDirection>('forward');
  const [isFinishing, setIsFinishing] = useState(false);

  const { hasValidAccount, refetchAccountStatus } =
    useMemberHasValidPlannerAccount();

  const defaultWorkingHours = intl.formatMessage({
    id: 'templates.smart_schedule.onboarding.default-working-hours',
    defaultMessage: 'Only schedule focus time Monday to Friday from 9am to 5pm',
    description: 'Prefilled default value for the working hours input',
  });
  const [workingHours, setWorkingHours] = useState<string>(defaultWorkingHours);
  const handleWorkingHoursChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setWorkingHours(e.target.value);
    },
    [],
  );

  const [selectedBoardAri, setSelectedBoardAri] = useState<string | null>(null);

  const [isProactiveEnabled, setIsProactiveEnabled] = useState(true);

  const [secondaryRule, setSecondaryRule] = useState<string>('');
  const [secondaryRuleType, setSecondaryRuleType] =
    useState<SmartScheduleOnboardingAiRuleType>('PLANNER_SELECTION');
  const [confirmedSecondaryRule, setConfirmedSecondaryRule] =
    useState<ConfirmedSecondaryRule | null>(null);

  // Compare against the prefilled (locale-aware) default so analytics records
  // whether the user customized it without ever sending the UGC text itself.
  const workingHoursCustomized = workingHours !== defaultWorkingHours;
  const hasBoardSelected = selectedBoardAri !== null;
  const hasSecondaryRule = confirmedSecondaryRule !== null;
  const confirmedSecondaryRuleType = confirmedSecondaryRule?.type ?? null;

  // Shared by every "next" CTA; only the click event that precedes each differs.
  const goToNextPage = useCallback(() => {
    setPageTransitionDirection('forward');
    setActivePage((page) => {
      let next = page + 1;
      // Skip the connect page when the member already has a connected account.
      while (
        next < PAGES.length &&
        PAGES[next] === 'connect' &&
        hasValidAccount
      ) {
        next += 1;
      }
      return next;
    });
  }, [hasValidAccount]);

  // The connect page has no "next" button — a successful connect advances it.
  const handleConnected = useCallback(() => {
    refetchAccountStatus();
    goToNextPage();
  }, [refetchAccountStatus, goToNextPage]);

  const handleBoardChange = useCallback(
    (boardAri: string | null) => {
      setSelectedBoardAri(boardAri);
      trackUIEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'onboardingBoardSelector',
        attributes: {
          screen: 'rulesScreen',
          hasBoardSelected: boardAri !== null,
        },
      });
    },
    [trackUIEvent],
  );

  const handleProactiveToggle = useCallback(() => {
    const next = !isProactiveEnabled;
    setIsProactiveEnabled(next);
    trackUIEvent({
      action: 'clicked',
      actionSubject: 'toggle',
      actionSubjectId: 'proactiveSchedulingToggle',
      attributes: { screen: 'rulesScreen', isEnabled: next },
    });
  }, [isProactiveEnabled, trackUIEvent]);

  const handleSecondaryRuleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setSecondaryRule(e.target.value);
    },
    [],
  );
  const handleSecondaryRuleTypeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextType = e.target.value as SmartScheduleOnboardingAiRuleType;
      setSecondaryRuleType(nextType);
      trackUIEvent({
        action: 'clicked',
        actionSubject: 'radioButton',
        actionSubjectId: 'ruleTypeRadioButton',
        attributes: { screen: 'rulesScreen', ruleType: nextType },
      });
    },
    [trackUIEvent],
  );
  // Clicking "Add rule" snapshots the current rule so only the confirmed value
  // is saved on finish, even if the user keeps typing afterwards.
  const handleAddSecondaryRule = useCallback(() => {
    setConfirmedSecondaryRule({
      rule: secondaryRule.trim(),
      type: secondaryRuleType,
    });
    trackUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'addRuleButton',
      attributes: { screen: 'rulesScreen', ruleType: secondaryRuleType },
    });
  }, [secondaryRule, secondaryRuleType, trackUIEvent]);

  const handleRuleTypeInfoClick = useCallback(
    (ruleType: SmartScheduleOnboardingAiRuleType) => {
      trackUIEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'ruleTypeInfoButton',
        attributes: { screen: 'rulesScreen', ruleType },
      });
    },
    [trackUIEvent],
  );

  // The rule is "saved" while the current value still matches the confirmed
  // snapshot; editing the rule or type clears it so it's clear whether the
  // latest edits are included.
  const isSecondaryRuleSaved =
    confirmedSecondaryRule?.rule === secondaryRule.trim() &&
    confirmedSecondaryRule?.type === secondaryRuleType;

  const handlePreviousPage = useCallback(() => {
    setPageTransitionDirection('backward');
    // Attribute the event to the screen the user is navigating away from.
    trackUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'previousOnboardingPageButton',
      attributes: { screen: SCREEN_BY_KEY[PAGES[activePage]] },
    });
    // Skip back over the connect page — once connected, "back" returns to the intro.
    const step = PAGES[activePage - 1] === 'connect' ? 2 : 1;
    setActivePage((page) => page - step);
  }, [activePage, trackUIEvent]);

  const handleStartScheduling = useCallback(() => {
    trackUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'startSchedulingButton',
      attributes: { screen: 'startScreen' },
    });
    goToNextPage();
  }, [trackUIEvent, goToNextPage]);

  const handleAddWorkingHours = useCallback(() => {
    trackUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'addWorkingHoursButton',
      attributes: { screen: 'workingHoursScreen', workingHoursCustomized },
    });
    goToNextPage();
  }, [trackUIEvent, goToNextPage, workingHoursCustomized]);

  const handleContinueToAllSet = useCallback(() => {
    trackUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'continueButton',
      attributes: { screen: 'rulesScreen' },
    });
    goToNextPage();
  }, [trackUIEvent, goToNextPage]);

  const handlePageEnter = useCallback((node: HTMLElement) => {
    node.removeAttribute('aria-hidden');
  }, []);

  const handlePageExit = useCallback((node: HTMLElement) => {
    node.setAttribute('aria-hidden', 'true');
  }, []);

  const showSaveErrorFlag = useCallback(() => {
    const memberAiSettingsUrl = getMemberAiSettingsUrl();

    showFlag({
      id: 'smartScheduleOnboardingSaveError',
      title: intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.save-error-title',
        defaultMessage: "We couldn't save some of your focus time settings.",
        description:
          'Error flag title shown when onboarding settings fail to save',
      }),
      description: intl.formatMessage({
        id: 'templates.smart_schedule.onboarding.save-error-description',
        defaultMessage: 'You can update them anytime on your AI settings page.',
        description: 'Error flag description pointing the user to AI settings',
      }),
      appearance: 'error',
      isAutoDismiss: false,
      actions: [
        {
          content: intl.formatMessage({
            id: 'templates.smart_schedule.onboarding.go-to-ai-settings',
            defaultMessage: 'Go to AI settings',
            description: 'Error flag action linking to the AI settings page',
          }),
          href: memberAiSettingsUrl,
          type: 'link',
          onClick: (event) => {
            event.preventDefault();
            navigate(memberAiSettingsUrl);
          },
        },
      ],
    });
  }, [intl]);

  // Persist everything the user entered in one batch, then continue with the
  // action that triggered onboarding. Saves are best-effort: a single error flag
  // is shown on partial failure but we still close and continue.
  const handleFinish = useCallback(async () => {
    setIsFinishing(true);

    trackUIEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'finishOnboardingButton',
      attributes: { screen: 'allSetScreen' },
    });

    const [boardSaved, proactiveSaved, workingHoursSaved] = await Promise.all([
      saveBoardPreference({
        preferredBoards: selectedBoardAri ? [selectedBoardAri] : [],
        includeInbox: true,
        silent: true,
      }),
      saveProactiveStatus(isProactiveEnabled, { silent: true }),
      workingHours.trim().length > 0
        ? upsertRule({
            rule: workingHours,
            type: 'PLANNER_SCHEDULING',
            silent: true,
          }).then((result) => result.success)
        : Promise.resolve(true),
    ]);

    // Create the optional secondary rule after the working hours rule so the two
    // creates don't compute the same position from a stale cache.
    let secondaryRuleSaved = true;
    if (confirmedSecondaryRule) {
      const result = await upsertRule({
        rule: confirmedSecondaryRule.rule,
        type: confirmedSecondaryRule.type,
        silent: true,
      });
      secondaryRuleSaved = result.success;
    }

    if (
      !boardSaved ||
      !proactiveSaved ||
      !workingHoursSaved ||
      !secondaryRuleSaved
    ) {
      showSaveErrorFlag();
    }

    // Fire after saves resolve so the track events reflect the final outcome.
    trackCompleted({
      hasBoardSelected,
      isProactiveEnabled,
      workingHoursCustomized,
      hasSecondaryRule,
      secondaryRuleType: confirmedSecondaryRuleType,
    });
    trackSaved({
      boardSaved,
      proactiveSaved,
      workingHoursRuleSaved: workingHoursSaved,
      secondaryRuleSaved,
    });

    setIsFinishing(false);
    onContinue();
    onClose();
  }, [
    saveBoardPreference,
    selectedBoardAri,
    saveProactiveStatus,
    isProactiveEnabled,
    upsertRule,
    workingHours,
    confirmedSecondaryRule,
    showSaveErrorFlag,
    onContinue,
    onClose,
    trackUIEvent,
    trackCompleted,
    trackSaved,
    hasBoardSelected,
    workingHoursCustomized,
    hasSecondaryRule,
    confirmedSecondaryRuleType,
  ]);

  // On the final page the user has reached the end of onboarding, so closing
  // via the X means the same thing as the primary CTA: persist their choices
  // instead of discarding them. Ignore the click while a save is already in
  // flight, matching the disabled state of the "All set" button.
  const handleCloseButtonClick = useCallback(() => {
    if (isFinishing) {
      return;
    }
    const wasCompleted = activePage === FINAL_PAGE;
    trackUIEvent({
      action: 'dismissed',
      actionSubject: 'onboarding',
      actionSubjectId: 'smartScheduleOnboarding',
      attributes: { screen: SCREEN_BY_KEY[PAGES[activePage]], wasCompleted },
    });
    if (wasCompleted) {
      void handleFinish();
      return;
    }
    onClose();
  }, [activePage, handleFinish, isFinishing, onClose, trackUIEvent]);

  // Fire the "saw onboarding" modal-viewed event exactly once per open. This is
  // the denominator for the onboarding funnel.
  useEffect(() => {
    trackModalViewed();
  }, [trackModalViewed]);

  // Fire a screen event whenever a page becomes active, including on
  // back-navigation. The start screen additionally carries the entry point.
  useEffect(() => {
    trackScreenViewed(SCREEN_BY_KEY[PAGES[activePage]]);
  }, [activePage, trackScreenViewed]);

  const renderActivePage = (): ReactNode => {
    switch (PAGES[activePage]) {
      case 'connect':
        return <ConnectAccount onConnected={handleConnected} />;
      case 'start':
        return <StartScheduling onNext={handleStartScheduling} />;
      case 'workingHours':
        return (
          <WorkingHours
            onNext={handleAddWorkingHours}
            workingHours={workingHours}
            onWorkingHoursChange={handleWorkingHoursChange}
          />
        );
      case 'rules':
        return (
          <SmartScheduleSettings
            onNext={handleContinueToAllSet}
            selectedBoardAri={selectedBoardAri}
            onBoardChange={handleBoardChange}
            isProactiveEnabled={isProactiveEnabled}
            onProactiveToggle={handleProactiveToggle}
            secondaryRule={secondaryRule}
            onSecondaryRuleChange={handleSecondaryRuleChange}
            secondaryRuleType={secondaryRuleType}
            onSecondaryRuleTypeChange={handleSecondaryRuleTypeChange}
            onAddSecondaryRule={handleAddSecondaryRule}
            isSecondaryRuleSaved={isSecondaryRuleSaved}
            onRuleTypeInfoClick={handleRuleTypeInfoClick}
          />
        );
      case 'allSet':
        return <AllSet onFinish={handleFinish} isFinishing={isFinishing} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      {...dialogProps}
      alignment="center"
      className={styles.dialog}
      closeOnEscape={false}
      closeOnOutsideClick={false}
      labelledBy="smart-schedule-onboarding-title"
      size="large"
    >
      <div className={styles.dialogLayout}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {activePage > 0 && (
              <Button
                appearance="subtle"
                className={styles.previousButton}
                onClick={handlePreviousPage}
                iconBefore={
                  <ChevronLeftIcon
                    label={intl.formatMessage({
                      id: 'templates.smart_schedule.previous',
                      defaultMessage: 'Previous',
                      description: 'Previous button label',
                    })}
                  />
                }
              />
            )}
          </div>
          <div className={styles.headerCenter}>
            <span>
              <AiSparkleIcon label="" />
            </span>
            <p>
              <FormattedMessage
                id="templates.smart_schedule.onboarding.dialog-title"
                defaultMessage="Schedule Focus Time"
                description="Title shown in the Smart Schedule onboarding dialog header"
              />
            </p>
          </div>
          <div className={styles.headerRight}>
            <DialogCloseButton onClick={handleCloseButtonClick} size="medium" />
          </div>
        </div>
        <div className={styles.contentWrapper}>
          {isReducedMotionEnabled ? (
            renderActivePage()
          ) : (
            <TransitionGroup
              className={cx(styles.pageTransition, {
                [styles.slideForward]: pageTransitionDirection === 'forward',
                [styles.slideBackward]: pageTransitionDirection === 'backward',
              })}
            >
              <CSSTransitionWithRef
                key={activePage}
                timeout={300}
                classNames={{
                  enter: styles.slideInStart,
                  enterActive: styles.slideInEnd,
                  exit: styles.slideOutStart,
                  exitActive: styles.slideOutEnd,
                }}
                onEnter={handlePageEnter}
                onExit={handlePageExit}
              >
                <div className={styles.page}>{renderActivePage()}</div>
              </CSSTransitionWithRef>
            </TransitionGroup>
          )}
        </div>
      </div>
    </Dialog>
  );
};
